/** 订单侧的纯逻辑（无 React）。
 *
 * 均价与手续费由后端 `/orders` 直接给（裁决 B10），这里**不再推导**；只做两件展示判断：
 * 该单的成交明细与成交缺口、「凭什么下的这一单」五项可追 + 版本位，缺项标「缺」。
 */

import type { G2Decision, G2Order, G2Trade, RunTrace } from "../api/g2";

/** 被拒的单也要留在表里并带原因——「没记账」不等于「没下单」。 */
export function isRejected(order: Pick<G2Order, "status">): boolean {
  return order.status === "REJECTED";
}

export function tradesOf(orderId: string, trades: G2Trade[]): G2Trade[] {
  return trades.filter((trade) => trade.client_order_id === orderId);
}

/** 成交明细的空态文案；有成交时返回 `null`。 */
export function fillGap(order: Pick<G2Order, "status">, trades: G2Trade[]): string | null {
  if (trades.length > 0) return null;
  if (isRejected(order)) return "被拒，无成交";
  return "无成交（未成交或账目缺口，去对账屏核对）";
}

/** 「凭什么」一项：`value` 为 `null` 即缺，`why` 写清缺在哪。 */
export interface TraceItem {
  label: string;
  value: string | null;
  why: string;
}

/** operations-console「逐单可追」五项：信号 / 研究流程 / 参数集 / 数据快照 / 风控校验记录。
 * `decision` 为 `undefined` 说明该单无决策留痕——整组都缺，不拿相邻订单顶上。 */
export function traceItems(decision: G2Decision | undefined): TraceItem[] {
  if (!decision) {
    const why = "该订单无决策留痕";
    return ["信号", "研究流程", "参数集", "数据快照", "风控校验记录"].map((label) => ({ label, value: null, why }));
  }
  const text = (value: string) => (value ? value : null);
  const adjustment = decision.risk_adjustment;
  return [
    {
      label: "信号",
      value: decision.signal_id
        ? `${decision.signal_id} · ${decision.direction || "?"} · 目标权重 ${Number.isFinite(decision.target_weight) ? decision.target_weight : "?"}`
        : null,
      why: "decisions.signal_id 为空",
    },
    {
      label: "研究流程",
      value: text(decision.research_flow),
      why: "decisions.research_flow 为空",
    },
    { label: "参数集", value: text(decision.params_digest), why: "decisions.params_digest 为空" },
    { label: "数据快照", value: text(decision.data_snapshot), why: "decisions.data_snapshot 为空" },
    {
      label: "风控校验记录",
      value: adjustment ? `数量调整：${JSON.stringify(adjustment)}` : null,
      why: "未记录逐层校验（risk_checks 待加；本单无数量调整）",
    },
  ];
}

/** 版本追溯位：研究流程版本、配置指纹、模型版本。缺的位显式标「缺」。 */
export function versionItems(decision: G2Decision | undefined, run: RunTrace | null): TraceItem[] {
  const versions = run?.versions ?? null;
  const modelVersion = versions && typeof versions["model_version"] === "string" ? (versions["model_version"] as string) : null;
  return [
    {
      label: "研究流程版本",
      value: decision?.research_version ?? null,
      why: "decisions.research_version 待加（DecisionRecord 落账时丢弃了信号来源 version）",
    },
    {
      label: "配置指纹",
      value: run?.config_digest ?? null,
      why: run ? "运行归档没有 config_digest" : "这本账未绑定运行，取不到运行归档",
    },
    {
      label: "模型版本",
      value: modelVersion,
      why: versions ? "信号合约无模型版本字段" : "该运行早于版本留痕（runs/{run_id}.versions 待加）",
    },
  ];
}
